import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AlarmsListSearchCriteria } from '@equipment/services/alarms-list-search-criteria.interface';
import { AvgChartDataQueryCriteria } from '@equipment/services/avg-chart-data-query-criteria.interface';
import { EquipmentChartsService } from '@equipment/services/equipment-charts.service';
import { EquipmentDetailsService } from '@equipment/services/equipment-details.service';
import { HistoryListSearchCriteria } from '@equipment/services/history-list-search-criteria.interface';
import { HistoryService } from '@equipment/services/history.service';
import { InstChartDataQueryCriteria } from '@equipment/services/inst-chart-data-query-criteria.interface';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { AvgChartData } from '@shared/models/avg-chart-data';
import { History } from '@shared/models/history';
import { InstChartData } from '@shared/models/inst-chart-data';
import { LanguageService } from '@shared/services/language.service';
import { PagedResult } from '@shared/services/paged-result';
import * as Highcharts from 'highcharts/highstock';
import HighchartsBoost from 'highcharts/modules/boost';
import noData from 'highcharts/modules/no-data-to-display';
import { ProfileService } from '@shared/services/profile.service';
import { Alarm } from '@shared/models/alarm';
import { Equipment } from '@shared/models/equipment';
import { EventRealTimeType, SocketService, SocketTopics } from '@shared/services/socket.service';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Permission } from '@users/domain/services/user.enums';
import { SeriesGrouperService } from '@equipment/services/series-grouper.service';
import { DateService } from '@shared/services/date.service';
import {
  FilterCategory
} from '@equipment/components/equipment-details/equipment-charts/dynamic-filter/dynamic-filter.component';
import {
  BASE_CHART_OPTIONS,
  COMMON_EVENT_SERIES,
  CommonSeriesChartService,
  CHART_DATA_TYPE,
  CHART_SERIES_TYPE
} from '@equipment/services/common-series-chart.service';
import { ChartConfigService } from '@equipment/services/chart-config.service';

HighchartsBoost(Highcharts);
noData(Highcharts);



export const COLORS = [
  '#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99',
  '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a',
  '#2f7ed8', '#0d233a', '#8bbc21', '#910000', '#1aadce',
  '#492970', '#f28f43', '#77a1e5', '#c42525', '#a6c96a',
  '#32b494', '#c3c96a', '#dcea2b'
];


@UntilDestroy()
@Component({
  selector: 'app-fluid-charts',
  templateUrl: './fluid-charts.component.html',
  styleUrls: ['./fluid-charts.component.scss'],
  providers: [SeriesGrouperService]
})
export class FluidChartsComponent implements OnInit, OnDestroy {
  private INST_QUERY_CRITERIA: InstChartDataQueryCriteria = {
    columns: ['average_reference', 'tank_weight', 'low_level_limit'],
    filters: {
      minDate: this.dateService.moment().startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    pagination: {
      page: 1,
      perPage: 30000
    }
  };

  private AVG_QUERY_CRITERIA: AvgChartDataQueryCriteria = {
    columns: ['average_reference', 'tank_weight', 'low_level_limit'],
    filters: {
      minDate: this.dateService.moment().subtract(6, 'days').startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    pagination: {
      page: 1,
      perPage: 30000
    }
  };

  private ALARMS_INST_QUERY_CRITERIA: AlarmsListSearchCriteria = {
    filters: {
      minDate: this.dateService.moment().startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    sort: {
      field: 'date',
      direction: 'ASC'
    },
    pagination: {
      page: 0,
      perPage: 30000
    }
  };

  private ALARMS_AVG_QUERY_CRITERIA: AlarmsListSearchCriteria = {
    filters: {
      minDate: this.dateService.moment().subtract(6, 'days').startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    sort: {
      field: 'date',
      direction: 'ASC'
    },
    pagination: {
      page: 0,
      perPage: 30000
    }
  };

  private HISTORY_INST_QUERY_CRITERIA: HistoryListSearchCriteria = {
    filters: {
      type: ['refill'],
      minDate: this.dateService.moment().startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    sort: {
      field: 'date',
      direction: 'ASC'
    },
    pagination: {
      page: 0,
      perPage: 30000
    }
  };

  private HISTORY_AVG_QUERY_CRITERIA: HistoryListSearchCriteria = {
    filters: {
      type: ['refill'],
      minDate: this.dateService.moment().subtract(6, 'days').startOf('day').toISOString(),
      maxDate: this.dateService.moment().endOf('day').toISOString()
    },
    sort: {
      field: 'date',
      direction: 'ASC'
    },
    pagination: {
      page: 0,
      perPage: 30000
    }
  };

  private chartSeriesTemp: any[];
  private alarmsData: PagedResult<any>;
  private refillsData: PagedResult<History>;
  private chartData: PagedResult<InstChartData | AvgChartData>;
  public FILTERS: FilterCategory[];
  protected INITIAL_FILTERS_FILTERED: any;
  private isLazyLoadOn = false;

  constructor(
    private dateService: DateService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private equipmentChartsService: EquipmentChartsService,
    private equipmentDetailsService: EquipmentDetailsService,
    private historyService: HistoryService,
    private profileService: ProfileService,
    private socketService: SocketService,
    private seriesGrouperService: SeriesGrouperService,
    private cdr: ChangeDetectorRef,
    private commonSeriesChartService: CommonSeriesChartService,
    private chartConfigService: ChartConfigService
  ) {
  }

  protected refresh = false;
  destroyRef = inject(DestroyRef);

  @ViewChild('matTab', { read: ElementRef, static: false }) matTab: ElementRef;
  @Input() minDate: any;
  @Input() maxDate: any;

  @Input() showCheckCard = true;
  public chartIsLoading = true;
  public filtersFormGroup = new FormGroup({});
  public rangeAvgFg = new FormGroup({
    start: new FormControl<Date | null>(this.dateService.moment().subtract(6, 'days').startOf('day').toDate()),
    end: new FormControl<Date | null>(this.dateService.moment().toDate())
  });
  public crtLang = '';
  public trInstant: (key: string | Array<string>, interpolateParams?: Object) => string | any;
  public Highcharts: typeof Highcharts = Highcharts;
  public chartOptions: Highcharts.Options;
  public chartDataType = CHART_DATA_TYPE;
  public activeTab = CHART_DATA_TYPE.AVG;
  public INITIAL_FILTERS: FilterCategory[] = [];

  private instCriteria: InstChartDataQueryCriteria;
  private avgCriteria: AvgChartDataQueryCriteria;
  private alarmsInstCriteria: AlarmsListSearchCriteria;
  private alarmsAvgCriteria: AlarmsListSearchCriteria;
  private historyInstCriteria: HistoryListSearchCriteria;
  private historyAvgCriteria: HistoryListSearchCriteria;

  private chartSeries: any[] = [];
  private minChartValue = 0;
  private chartHasError: boolean;
  public readyToRender = true;

  @Input() public equipmentId: string;
  @Input() public equipment: Equipment;
  @ViewChild('graphCard') graphCard: ElementRef<HTMLInputElement>;
  private instDatePickerMinDate: Date;
  private listenToDataChange: Subscription;
  chartInstance: any;

  protected readonly Permission = Permission;

  protected readonly FormControl = FormControl;

  public isEquipmentType(types: string[]) {
    return types.includes(this.equipment?.main_device_id?.hardware_version.split('.')[0] === '1' ? 'DNI' : 'PolarBox');
  }

  public async ngOnInit(): Promise<void> {
    this.INITIAL_FILTERS = ([
      {
        key: 'fluid_levels',
        translationSlug: 'website.equipment.detail.charts.fluid.fluid_levels',
        checked: true,
        filters: [
          {
            key: 'moving_average',
            value: 'tank_weight',
            type: CHART_SERIES_TYPE.MOVING_AVERAGE,
            translationSlug: 'website.equipment.detail.charts.fluid.moving_average',
            valueSuffix: 'weight_large',
            checked: true,
            showInChart: true,
            colorIndex: 0,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          },
          {
            key: 'average_reference',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.average_reference',
            valueSuffix: 'weight_large',
            checked: true,
            showInChart: true,
            colorIndex: 0,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          },
          {
            key: 'tank_weight',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.tank_weight',
            valueSuffix: 'weight_large',
            checked: true,
            showInChart: true,
            colorIndex: 1,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          },
          {
            key: 'low_level_limit',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.low_level_limit',
            valueSuffix: 'weight_large',
            checked: true,
            showInChart: true,
            colorIndex: 4,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          }
        ]
      },
      {
        key: 'temperatures',
        translationSlug: 'website.equipment.detail.charts.fluid.temperatures',
        checked: false,
        filters: [
          {
            key: 'input_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.input_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 3,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2
          },
          {
            key: 'output_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.output_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 4,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2
          },
          {
            key: 'external_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.external_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 6,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2
          },
          {
            key: 'suction_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.suction_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 7,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2,
            hide: !this.isEquipmentType(['PolarBox'])
          },
          {
            key: 'discharge_temp_measured',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.discharge_temp_measured',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 8,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2,
            hide: !this.isEquipmentType(['PolarBox'])
          },
          {
            key: 'hp_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.hp_temp',
            hideValueSuffix: true,
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 5,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            yAxis: 2,
            tooltipSeries: [
              {
                key: 'hp',
                type: CHART_SERIES_TYPE.Y,
                translationSlug: 'website.equipment.detail.charts.fluid.hp',
                valueSuffix: 'pressure',
                checked: true,
                showInChart: false,
                dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG]
              }
            ]
          },
          {
            key: 'hp_gas_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.hp_gas_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 21,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            yAxis: 2,
            disable: !this.isEnabledHpGasTemp(),
            tooltipSeries: null
          },
          {
            key: 'lp_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.lp_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            hide: !this.isEquipmentType(['PolarBox']),
            colorIndex: 9,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            yAxis: 2,
            tooltipSeries: [
              {
                key: 'lp',
                type: CHART_SERIES_TYPE.Y,
                translationSlug: 'website.equipment.detail.charts.fluid.lp',
                valueSuffix: 'pressure',
                checked: true,
                showInChart: false,
                dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG]
              }
            ]
          }
        ]
      },
      {
        key: 'complementary_temperatures',
        translationSlug: 'website.equipment.detail.charts.fluid.complementary_temperatures',
        checked: false,
        filters: [
          {
            key: 'suction_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.suction_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 7,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            disable: !this.equipmentHasModuleEnergy(),
            yAxis: 2,
            hide: !this.isEquipmentType(['DNI'])
          },
          {
            key: 'discharge_temp_measured',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.discharge_temp_measured',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 8,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            disable: !this.equipmentHasModuleEnergy(),
            yAxis: 2,
            hide: !this.isEquipmentType(['DNI'])
          }, {
            key: 'discharge_temp_calculated',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.discharge_temp_calculated',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 8,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            yAxis: 2

          },
          {
            key: 'discharge_temp_isentropic',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.discharge_temp_isentropic',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 20,
            dataType: [CHART_DATA_TYPE.INST],
            tooltipSeries: null
          },
          {
            key: 'lp_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.lp_temp',
            valueSuffix: 'temperature',
            hideValueSuffix: true,
            checked: false,
            showInChart: true,
            hide: !this.isEquipmentType(['DNI']),
            disable: !this.equipmentHasModuleEnergy(),
            colorIndex: 9,
            dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
            yAxis: 2,
            tooltipSeries: [
              {
                key: 'lp',
                type: CHART_SERIES_TYPE.Y,
                translationSlug: 'website.equipment.detail.charts.fluid.lp',
                valueSuffix: 'temperature',
                checked: true,
                showInChart: false,
                dataType: [CHART_DATA_TYPE.INST]
              }
            ]
          },
          {
            key: 'weather_temp',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.weather_temp',
            valueSuffix: 'temperature',
            checked: false,
            showInChart: true,
            colorIndex: 22,
            dataType: [CHART_DATA_TYPE.AVG],
            tooltipSeries: null,
            hide: !this.profileService.isAdmin()
          },
        ]
      },
      {
        key: 'advanced_options',
        translationSlug: 'website.equipment.detail.charts.fluid.advanced_options',
        checked: false,
        filters: [
          {
            key: 'compressor_state',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.compressor_state',
            valueSuffix: '',
            checked: false,
            showInChart: true,
            colorIndex: 10,
            dataType: [CHART_DATA_TYPE.INST],
            tooltipSeries: null
          },
          {
            key: 'under_low_level_number',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.under_low_level_number',
            valueSuffix: '',
            checked: false,
            showInChart: false,
            colorIndex: 11,
            dataType: [CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          },
          {
            key: 'flash_gas_number',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.flash_gas_number',
            valueSuffix: '',
            checked: false,
            showInChart: true,
            colorIndex: 12,
            dataType: [CHART_DATA_TYPE.AVG],
            tooltipSeries: [
              {
                key: 'flash_gas_with_compressor_on_number',
                type: CHART_SERIES_TYPE.Y,
                translationSlug: 'website.equipment.detail.charts.fluid.flash_gas_with_compressor_on_number',
                valueSuffix: '',
                checked: false,
                showInChart: false,
                dataType: [CHART_DATA_TYPE.AVG]
              }
            ]
          },
          {
            key: 'compressor_state_on_number',
            type: CHART_SERIES_TYPE.Y,
            translationSlug: 'website.equipment.detail.charts.fluid.lp',
            valueSuffix: 'nb',
            timeUnit : 'h',
            checked: false,
            showInChart: false,
            colorIndex: 13,
            dataType: [CHART_DATA_TYPE.AVG],
            tooltipSeries: null
          }
        ]
      },
      COMMON_EVENT_SERIES
    ]);

    if (this.profileService.isAdmin()) {
      if (!this.INITIAL_FILTERS.find(f => f.key === 'matelex_options')) {
        this.INITIAL_FILTERS.push({
          key: 'matelex_options',
          translationSlug: 'website.equipment.detail.charts.fluid.matelex_options',
          checked: false,
          filters: [
            {
              key: 'number_samples',
              type: CHART_SERIES_TYPE.Y,
              translationSlug: 'website.equipment.detail.charts.fluid.number_samples',
              valueSuffix: '',
              checked: false,
              showInChart: true,
              colorIndex: 14,
              dataType: [CHART_DATA_TYPE.AVG],
              tooltipSeries: null
            },
            {
              key: 'statistics_3days_limit',
              type: CHART_SERIES_TYPE.Y,
              translationSlug: 'website.equipment.detail.charts.fluid.statistics_3days_limit',
              valueSuffix: '',
              checked: false,
              showInChart: true,
              colorIndex: 15,
              dataType: [CHART_DATA_TYPE.AVG],
              tooltipSeries: null
            },
            {
              key: 'old_slope_3_days',
              type: CHART_SERIES_TYPE.Y,
              translationSlug: 'website.equipment.detail.charts.fluid.old_slope_3_days',
              valueSuffix: '',
              checked: false,
              showInChart: true,
              colorIndex: 16,
              dataType: [CHART_DATA_TYPE.AVG],
              tooltipSeries: null
            }
          ]
        });
      }
    }
    this.refreshFilter();
    this.crtLang = this.languageService.currentLanguage;
    this.trInstant = this.translateService.instant;
    this.trInstant = this.trInstant.bind(this.translateService);
    this.instDatePickerMinDate = this.dateService.moment(new Date()).subtract(3, 'months').toDate();

    if (this.minDate && this.maxDate) {
      this.avgCriteria.filters.minDate = this.minDate.toISOString();
      this.avgCriteria.filters.maxDate = this.maxDate.toISOString();

      this.instCriteria.filters.minDate = this.dateService.moment(this.maxDate).startOf('day').toISOString();
      this.instCriteria.filters.maxDate = this.dateService.moment(this.maxDate).endOf('day').toISOString();
    }

    this.buildDateFilters();
    await this.refreshAllData();
    this.readyToRender = true;
    this.chartIsLoading = false;
    await this.buildChart();
    this.enableRealtime();

    this.seriesGrouperService.flagZoom$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({
                                                                                               flagStart,
                                                                                               flagEnd
                                                                                             }) => {
      Highcharts.charts.filter(Boolean)[0].xAxis[0].setExtremes(flagStart, flagEnd);
    });
  }


  private enableRealtime(): void {
    const eventType = this.activeTab === CHART_DATA_TYPE.AVG ? EventRealTimeType.AVG_DATAS : EventRealTimeType.INST_DATAS;
    const socketTopic = this.activeTab === CHART_DATA_TYPE.AVG ? SocketTopics.AvgDatas : SocketTopics.InstDatas;
    this.socketService.unsubscribeToBackend(this.equipmentId, eventType === EventRealTimeType.AVG_DATAS ? EventRealTimeType.INST_DATAS : EventRealTimeType.AVG_DATAS);
    this.socketService.subscribeToBackend(this.equipmentId, eventType);
    this.listenToDataChange = this.socketService.on(socketTopic)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        console.log('refresh ' + socketTopic + ' datas');
        await this.refreshAllData();
        await this.refreshChart(this.FILTERS);
      });
  }


  private refreshFilter(): void {
    this.instCriteria = JSON.parse(JSON.stringify(this.INST_QUERY_CRITERIA));
    this.avgCriteria = JSON.parse(JSON.stringify(this.AVG_QUERY_CRITERIA));
    this.alarmsInstCriteria = JSON.parse(JSON.stringify(this.ALARMS_INST_QUERY_CRITERIA));
    this.alarmsAvgCriteria = JSON.parse(JSON.stringify(this.ALARMS_AVG_QUERY_CRITERIA));
    this.historyInstCriteria = JSON.parse(JSON.stringify(this.HISTORY_INST_QUERY_CRITERIA));
    this.historyAvgCriteria = JSON.parse(JSON.stringify(this.HISTORY_AVG_QUERY_CRITERIA));
    this.FILTERS = this.commonSeriesChartService.applyFilterToFilterCategory(this.INITIAL_FILTERS, this.activeTab);
    this.INITIAL_FILTERS_FILTERED = this.commonSeriesChartService.applyFilterToFilterCategory(this.INITIAL_FILTERS, this.activeTab);
  }


  public async selectedTabChange(event: any): Promise<void> {
    this.activeTab = event.index;
    this.chartIsLoading = true;
    this.refreshFilter();
    await this.updateDataAndChart();
    this.enableRealtime();
  }

  private async updateDataAndChart(): Promise<void> {
    const chart = this.Highcharts.charts.filter(Boolean)[0];
    this.chartIsLoading = true;
    chart?.zoomOut();
    await this.refreshAllData();
    this.buildChartSeries();
    this.buildChartOptions();
    await this.buildChart();
    this.chartIsLoading = false;
  }


  protected async refreshAllData(): Promise<void> {
    this.buildCriteriaColumnsToQuery();

    const [chartData, alarmsData, refillsData] = await Promise.all([
      this.loadChartData(this.activeTab),
      this.loadAlarmsData(this.activeTab),
      this.loadHistoryData(this.activeTab)
    ]);

    this.chartData = chartData;
    this.alarmsData = alarmsData;
    this.refillsData = refillsData;
  }

  public isEnabledHpGasTemp(): boolean {
    return (this.isEquipmentType(['DNI']) && this.equipment?.fluid?.id === 'CO2' && this.equipmentHasModuleEnergy()) || (this.isEquipmentType(['PolarBox']) && this.equipment?.fluid?.id === 'CO2');
  }

  private async buildChart(): Promise<void> {
    this.buildChartSeries();
    this.buildChartOptions();
    await this.refreshChart(this.FILTERS);
  }

  private buildDateFilters(): void {

    const instDateFilterFormControl = new FormControl(this.maxDate ? new Date(this.dateService.moment(this.maxDate).valueOf()) : new Date(this.dateService.moment().valueOf()));
    this.filtersFormGroup.addControl('instDateFilter', instDateFilterFormControl);


    instDateFilterFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (value) => {
      const instDate = this.dateService.moment(this.filtersFormGroup.get('instDateFilter').value);
      const minDate = instDate.startOf('day').toISOString();
      const maxDate = instDate.endOf('day').toISOString();

      this.instCriteria.filters.minDate = minDate;
      this.instCriteria.filters.maxDate = maxDate;

      this.alarmsInstCriteria.filters.minDate = minDate;
      this.alarmsInstCriteria.filters.maxDate = maxDate;

      this.historyInstCriteria.filters.minDate = minDate;
      this.historyInstCriteria.filters.maxDate = maxDate;
      this.chartIsLoading = true;

      if (instDate.isSame(this.dateService.moment(), 'day')) {
        this.enableRealtime();
      } else {
        this.disableRealtime(EventRealTimeType.INST_DATAS);
      }
      await this.updateDataAndChart();
    });

    this.filtersFormGroup.get('instDateFilter').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (value) => {
      // ... existing code ...
    });
  }

  public async onDateRangeChange(event: any): Promise<void> {
    if (event && event.start && event.end) {
      this.rangeAvgFg.setValue({
        start: event.start,
        end: event.end
      }, { emitEvent: false }); // Important: éviter d'émettre un autre événement valueChanges

      const start = this.dateService.moment(event.start).startOf('day').toISOString();
      this.avgCriteria.filters.minDate = start;
      this.alarmsAvgCriteria.filters.minDate = start;
      this.historyAvgCriteria.filters.minDate = start;

      const end = this.dateService.moment(event.end).endOf('day').toISOString();
      this.avgCriteria.filters.maxDate = end;
      this.alarmsAvgCriteria.filters.maxDate = end;
      this.historyAvgCriteria.filters.maxDate = end;
      this.isLazyLoadOn = this.dateService.moment(end).diff(start, 'day') > 30;
      this.buildCriteriaColumnsToQuery();

      if (this.dateService.moment(event.end).isSame(this.dateService.moment(), 'day')) {
        this.enableRealtime();
      } else {
        this.disableRealtime(EventRealTimeType.AVG_DATAS);
      }

      await this.updateDataAndChart();
    }
  }

  private disableRealtime(topic: EventRealTimeType) {
    this.socketService.unsubscribeToBackend(this.equipmentId, topic);
    this.listenToDataChange?.unsubscribe();
  }

  private equipmentHasModuleEnergy(): boolean {
    return this.equipment?.childDevicesOfMainDevice?.some((device) => device.type === 'energy');
  }
  private groupSeries(minDate: Date, maxDate: Date): any[] {
    return this.seriesGrouperService.groupSeries(this.chartSeriesTemp, minDate, maxDate, ['level', 'statistical']);
  }

  private buildChartOptions(): void {
    const ctx = this;
    this.chartOptions = {
      ...BASE_CHART_OPTIONS, // Spread the base properties
      colors: COLORS,
      tooltip: {
        ...BASE_CHART_OPTIONS.tooltip,
        formatter() {
          return ctx.commonSeriesChartService.tooltipFormatted(this, ctx);
        },
      },
      time: {
        timezone: this.dateService.getBrowserTimezone() // Add component-specific properties
      },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        events: {
          afterSetExtremes(event) {
            ctx.commonSeriesChartService.refreshSeriesByDateRange(event, ctx);
          }
        },
      },

      yAxis: [
        {
          title: { text: this.trInstant('website.equipment.detail.charts.values') },
          events: {
            afterSetExtremes: event => {
              this.minChartValue = event.min;
            }
          }
        },
        {
          title: { text: '' },
          labels: { enabled: false }
        },
        {
          opposite: true,
          title: { text: this.trInstant('Température (en °)') },
          labels: { enabled: false }
        }
      ],
      series: this.chartSeries, // Add dynamic series
      lang: {
        noData: this.chartHasError
          ? this.trInstant('website.equipment.detail.charts.error')
          : this.trInstant('website.equipment.detail.charts.no_data')
      }
    };
  }



  private buildCriteriaColumnsToQuery(): void {
    this.instCriteria.columns = this.avgCriteria.columns = this.FILTERS
      .map(filterCategory => filterCategory.filters)
      .flat()
      .filter(
        filterItem =>
          (this.isLazyLoadOn ? filterItem.checked : true) &&
          (filterItem.type === CHART_SERIES_TYPE.Y || filterItem.type === CHART_SERIES_TYPE.MOVING_AVERAGE) &&
          filterItem.dataType.includes(this.activeTab)
      )
      .map(filterItem => filterItem.value ?? filterItem.key);


    const tooltipSeriesColumns = this.FILTERS
      .map(filterCategory => filterCategory.filters)
      .flat()
      .filter(
        filterItem =>
          (this.isLazyLoadOn ? filterItem.checked : true) &&
          filterItem.type === CHART_SERIES_TYPE.Y &&
          filterItem.dataType.includes(this.activeTab) &&
          filterItem.tooltipSeries !== null
      )
      .map(filterItem => filterItem.tooltipSeries)
      .flat()
      .filter(tooltipSeries => tooltipSeries.dataType.includes(this.activeTab))
      .map(tooltipSeries => tooltipSeries.key);

    this.instCriteria.columns = [...this.instCriteria.columns, ...tooltipSeriesColumns];
    this.avgCriteria.columns = [...this.avgCriteria.columns, ...tooltipSeriesColumns];
  }

  buildChartSeries(): void {
    this.chartSeries = this.commonSeriesChartService.buildChartSeries(this.chartData, this.alarmsData,
      this.refillsData, this.FILTERS, this.activeTab,
      this.minChartValue);
    let date = this.avgCriteria.filters;
    if (this.activeTab === CHART_DATA_TYPE.INST) {
          date = this.instCriteria.filters;
        }
    this.chartSeriesTemp = this.chartSeries;
    this.chartSeries = this.groupSeries(new Date(this.dateService.moment(date.minDate).toISOString()), new Date(this.dateService.moment(date.maxDate).toISOString()));
  }

  async refreshChart(changedValue: any): Promise<void> {
    const chart = this.Highcharts.charts.filter(Boolean)[0];
    if (!chart) {
      return;
    }
    this.FILTERS = changedValue;
    if (this.isLazyLoadOn) {
      chart.showLoading();
      this.buildCriteriaColumnsToQuery();
      this.chartData = await this.loadChartData(this.activeTab);

    }
    this.buildChartSeries();
    this.chartConfigService.updateChartSeries(this.FILTERS, chart,  chart.series, this.chartSeries);
    chart.hideLoading();
    this.cdr.detectChanges();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.chartInstance) {
      this.chartInstance.reflow();
    }
  }

  chartCallback(chart) {
    this.chartInstance = chart;
  }
  private async loadChartData(dataType: CHART_DATA_TYPE): Promise<PagedResult<InstChartData | AvgChartData>> {
    let chartResult;
    if (dataType === CHART_DATA_TYPE.INST) {
      chartResult = await this.loadInstChartData();
    } else if (dataType == CHART_DATA_TYPE.AVG) {
      chartResult = await this.loadAvgChartData();
    }
    return chartResult;
  }

  private async loadAlarmsData(dataType: CHART_DATA_TYPE): Promise<PagedResult<any>> {
    let alarmsResult;
    if (dataType === CHART_DATA_TYPE.INST) {
      alarmsResult = await this.loadAlarmsInstData();
    } else if (dataType == CHART_DATA_TYPE.AVG) {
      alarmsResult = await this.loadAlarmsAvgData();
    }
    alarmsResult.itemsPage = alarmsResult.itemsPage?.filter((alarm: Alarm) => alarm?.alarm_type?.id !== 'disconnected');
    return alarmsResult;
  }

  private async loadHistoryData(dataType: CHART_DATA_TYPE): Promise<PagedResult<History>> {
    let historyResult;
    if (dataType === CHART_DATA_TYPE.INST) {
      historyResult = await this.loadHistoryInstData();
    } else if (dataType == CHART_DATA_TYPE.AVG) {
      historyResult = await this.loadHistoryAvgData();
    }
    return historyResult;
  }

  private async loadInstChartData(): Promise<PagedResult<InstChartData>> {
    this.chartHasError = false;
    try {
      const instChartResult = await this.equipmentChartsService.getFluidInstChartData(this.equipmentId, this.instCriteria);
      return instChartResult;
    } catch (error) {
      this.chartHasError = true;
      return { itemsPage: [] } as PagedResult<InstChartData>;
    }
  }

  private async loadAvgChartData(): Promise<PagedResult<AvgChartData>> {
    this.chartHasError = false;
    try {
      const avgChartResult = await this.equipmentChartsService.getFluidAvgChartData([this.equipmentId], this.avgCriteria);
      return avgChartResult;
    } catch (error) {
      this.chartHasError = true;
      return { itemsPage: [] } as PagedResult<AvgChartData>;
    }
  }
  private async loadAlarmsInstData(): Promise<PagedResult<any>> {
    const alarmsResult = await this.equipmentDetailsService.getEquipmentActiveAlarms(this.equipmentId, this.alarmsInstCriteria);
    return alarmsResult;
  }

  private async loadAlarmsAvgData(): Promise<PagedResult<any>> {
    const alarmsResult = await this.equipmentDetailsService.getEquipmentActiveAlarms(this.equipmentId, this.alarmsAvgCriteria);
    return alarmsResult;
  }

  private async loadHistoryInstData(): Promise<PagedResult<History>> {
    const historyResult = await this.historyService.getHistoryForEquipment(this.equipmentId, this.historyInstCriteria);
    return historyResult;
  }

  private async loadHistoryAvgData(): Promise<PagedResult<History>> {
    const historyResult = await this.historyService.getHistoryForEquipment(this.equipmentId, this.historyAvgCriteria);
    return historyResult;
  }
  public instDatePickerFilter = (date: Date | null): boolean => {
    if (!date) {
      return true;
    }
    if (this.dateService.moment(date).isBefore(this.instDatePickerMinDate)) {
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.socketService.unsubscribeToBackend(this.equipmentId, EventRealTimeType.AVG_DATAS);
    this.socketService.unsubscribeToBackend(this.equipmentId, EventRealTimeType.INST_DATAS);
    this.listenToDataChange?.unsubscribe();
  }
}
